import React, { useEffect, useState } from "react";
import { Helmet } from 'react-helmet-async';
import { useTranslation } from "../../context/TranslationContext";
import { useCurrency } from "../../context/CurrencyContext";
import AOS from 'aos';
import 'aos/dist/aos.css';
import './home.css'; // Reusing home styles for consistency
import { useNavigate } from "react-router-dom";
import ApiProvider from "../../apiProvider/categoryApi";
import { IMAGE_URL } from "../../network/apiClient";
import FilterSidebar from "../../components/FilterSidebar";

const KidsPage = () => {
    const { translateSync } = useTranslation();
    const { getCurrencySymbol, getProductPrice, getProductMrp, formatPriceOnly } = useCurrency();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [totalResults, setTotalResults] = useState(0);
    const [loading, setLoading] = useState(true);
    const [limit, setLimit] = useState(20);
    const [hasMore, setHasMore] = useState(true);
    const [activeFilters, setActiveFilters] = useState({
        priceRange: { min: 0, max: 100000 },
        selectedCategories: [],
        selectedBrands: []
    });
    const [availableSubCategories, setAvailableSubCategories] = useState([]);
    const [availableBrands, setAvailableBrands] = useState([]);
    const [categoryId, setCategoryId] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        AOS.init({ duration: 1000, once: true });
        fetchKidsProducts(20);
    }, []);

    useEffect(() => {
        if (categoryId) {
            const fetchSubCategories = async () => {
                const brandId = activeFilters.selectedBrands?.[0] || "";
                const subCatResult = await ApiProvider.getSubCategoryList(categoryId, brandId);
                if (subCatResult?.status && subCatResult?.response?.data) {
                    setAvailableSubCategories(subCatResult.response.data.items || []);
                }
            };
            fetchSubCategories();
        }
    }, [categoryId, activeFilters.selectedBrands]);

    const fetchKidsProducts = async (currentLimit = limit, filters = activeFilters) => {
        try {
            if (currentLimit === 20) setLoading(true);

            // 1. Get Category ID if not already set
            let currentCatId = categoryId;
            if (!currentCatId) {
                const catResult = await ApiProvider.getCategoryList();
                if (catResult?.status && catResult?.response?.data) {
                    const kidsCat = catResult.response.data.find(c =>
                        c.slug?.toLowerCase() === 'kids' || c.name?.toLowerCase().includes('kids')
                    );
                    if (kidsCat) {
                        currentCatId = kidsCat._id;
                        setCategoryId(currentCatId);
                    }
                }
            }

            // 2. Fetch Brands (only on first load)
            if (availableBrands.length === 0) {
                const brandResult = await ApiProvider.getBrandList();
                if (brandResult?.status && brandResult?.response?.data) {
                    setAvailableBrands(brandResult.response.data || []);
                }
            }

            // 3. Fetch/Filter Subcategories dynamically based on selected brand
            if (currentCatId) {
                const selectedBrandId = filters.selectedBrands?.[0];
                if (selectedBrandId) {
                    const brandProdResult = await ApiProvider.getCategoryDet(currentCatId, 1000, 0, { brandId: selectedBrandId });
                    if (brandProdResult?.status && brandProdResult?.response?.data) {
                        const uniqueSubsMap = new Map();
                        brandProdResult.response.data.forEach(product => {
                            product.subCategoryDetails?.forEach(sub => {
                                if (sub && sub._id) {
                                    uniqueSubsMap.set(sub._id, sub);
                                }
                            });
                        });
                        const subCategoriesForBrand = Array.from(uniqueSubsMap.values());
                        subCategoriesForBrand.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
                        setAvailableSubCategories(subCategoriesForBrand);
                    }
                } else {
                    const subCatResult = await ApiProvider.getSubCategoryList(currentCatId);
                    if (subCatResult?.status && subCatResult?.response?.data) {
                        setAvailableSubCategories(subCatResult.response.data.items || []);
                    }
                }
            }

            if (currentCatId) {
                // 3. Prepare API Filters
                const apiFilters = {
                    subCategoryId: filters.selectedCategories?.[0],
                    brandId: filters.selectedBrands?.[0]
                };
                if (filters.priceRange && (filters.priceRange.min !== 0 || filters.priceRange.max !== 100000)) {
                    apiFilters.priceFromRange = filters.priceRange.min;
                    apiFilters.priceToRange = filters.priceRange.max;
                }

                // 4. Fetch Products
                const prodResult = await ApiProvider.getCategoryDet(currentCatId, currentLimit, 0, apiFilters);
                if (prodResult?.status && prodResult?.response?.data) {
                    const newProducts = prodResult.response.data;
                    setProducts(newProducts);
                    setTotalResults(prodResult.response.total || 0);

                    setHasMore(newProducts.length >= currentLimit);
                }
            }
        } catch (error) {
            console.error("Error fetching kids products:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleShowMore = () => {
        const newLimit = limit + 20;
        setLimit(newLimit);
        fetchKidsProducts(newLimit);
    };

    const handleProductClick = (id) => {
        navigate(`/kids/${id}`);
    };

    const handleFilterChange = (filters) => {
        setActiveFilters(filters);
        setLimit(20);
        fetchKidsProducts(20, filters);
    };

    return (
        <>
            <Helmet>
                <title>Kids Collection | Colours Boutique</title>
                <meta name="description" content="Shop our exclusive collection of traditional wear for kids. Silk shirts, Pattu Pavadai, and more." />
            </Helmet>

            <div className="home-saree-container" style={{ paddingTop: "100px", minHeight: "100vh" }}>
                <section className="saree-section container-fluid px-lg-5">
                    <div className="row g-4 mt-0">
                        {/* Filter Sidebar - Fixed on left */}
                        <div className="col-lg-3 sidebar-column">
                            <FilterSidebar
                                onFilterChange={handleFilterChange}
                                availableCategories={availableSubCategories}
                                availableBrands={availableBrands}
                            />
                        </div>

                        {/* Product Area - col-lg-9 */}
                        <div className="col-lg-9">
                            <div className="saree-section-header" data-aos="fade-down">
                                <h2 className="saree-section-title">Kids Collection</h2>
                                <p className="saree-section-subtitle">Little Trends, Big Smiles</p>
                            </div>

                            {/* Active Filters Display */}
                            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                                <div className="active-tags d-flex align-items-center gap-3">
                                    {activeFilters.priceRange && (
                                        <span style={{ fontSize: '0.85rem', color: '#666', background: '#f5f5f5', padding: '4px 12px', borderRadius: '15px' }}>
                                            {getCurrencySymbol()} {formatPriceOnly(activeFilters.priceRange.min).toLocaleString()} — {getCurrencySymbol()} {formatPriceOnly(activeFilters.priceRange.max).toLocaleString()}
                                        </span>
                                    )}
                                    <span className="text-muted small">Showing {totalResults} results</span>
                                </div>
                            </div>

                            {loading ? (
                                <div className="text-center mt-5">Loading Collections...</div>
                            ) : products.length > 0 ? (
                                <div className="collection-grid mt-2">
                                    {products.map((item, idx) => {
                                        // Determine image to show
                                        const imgData = item.customerAttribute?.rowData?.[0]?.images?.[0] || item.productImage?.[0] || item.additionalImage?.[0];
                                        const imgSrc = imgData ? `${IMAGE_URL}${imgData.docPath}/${imgData.docName}` : '/img/logo/logo.png';

                                        return (
                                            <div
                                                key={idx}
                                                className="collection-item"
                                                data-aos="zoom-in"
                                                data-aos-delay={idx * 30}
                                                onClick={() => handleProductClick(item._id)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div className="collection-image-wrapper">
                                                    <img
                                                        src={imgSrc}
                                                        alt={item.productName}
                                                        className="collection-image"
                                                        onError={(e) => { e.target.src = '/img/logo/logo.png'; }}
                                                    />
                                                </div>

                                                {(() => {
                                                    const firstRow = item.customerAttribute?.rowData?.[0];
                                                    const priceValue = getProductPrice(item, firstRow);
                                                    const mrpValue = getProductMrp(item, firstRow);
                                                    const displayPrice = priceValue > 0 ? priceValue : null;
                                                    const displayMrp = mrpValue > 0 ? mrpValue : null;
                                                    const discount = displayMrp && displayPrice && displayMrp > displayPrice
                                                        ? Math.round(((displayMrp - displayPrice) / displayMrp) * 100)
                                                        : 0;

                                                    return (
                                                        <div className="text-start mt-3">
                                                            <h4 style={{
                                                                fontSize: '1.15rem',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                marginBottom: '8px',
                                                                lineHeight: '1.4',
                                                                textTransform: 'capitalize'
                                                            }}>
                                                                {item.productName}
                                                            </h4>                                                             <div className="d-flex justify-content-between align-items-center mt-2">
                                                                <div className="d-flex flex-wrap align-items-baseline gap-2">
                                                                    {priceValue === null ? (
                                                                        <span className="text-muted" style={{ fontSize: '0.9rem', fontStyle: 'italic' }}>
                                                                            Currency not available
                                                                        </span>
                                                                    ) : (
                                                                        <>
                                                                            <span className="fw-bold" style={{ fontSize: '1.1rem', color: '#B12704' }}>
                                                                                {getCurrencySymbol()}{displayPrice ? displayPrice.toLocaleString() : "0.00"}
                                                                            </span>
                                                                            {/* {displayMrp && (
                                                                                <span className="text-muted text-decoration-line-through" style={{ fontSize: '0.85rem' }}>
                                                                                    {getCurrencySymbol()}{displayMrp.toLocaleString()}
                                                                                </span>
                                                                            )} */}
                                                                        </>
                                                                    )}
                                                                </div>
                                                                {/* <div className="text-end" style={{ minWidth: '40px' }}>
                                                                    {discount > 0 && (
                                                                        <span className="px-1 text-white" style={{ fontSize: '0.7rem', backgroundColor: '#222', borderRadius: '2px' }}>
                                                                            {discount}% OFF
                                                                        </span>
                                                                    )}
                                                                </div> */}
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-5 w-100">
                                    <p className="text-muted" style={{ fontSize: '1.2rem', letterSpacing: '1px' }}>{translateSync('No products found in this collection.')}</p>
                                </div>
                            )}

                            {!loading && hasMore && products.length > 0 && (
                                <div className="text-center mt-5">
                                    <button
                                        className="btn btn-dark px-5 py-2 text-uppercase rounded-0"
                                        onClick={handleShowMore}
                                        style={{ letterSpacing: '2px', fontWeight: 'bold' }}
                                    >
                                        {translateSync('Show More')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default KidsPage;
